import { OddsChangeRules } from 'features/coupon/interface';
import { TFunction } from 'react-i18next';
import { APP_URL } from 'shared/constants/common';
import { EventType, PlaceBet } from 'shared/interface';
import {
	CouponDetailStatusCode,
	CouponStatusCodeToStatusMap,
	getParticipants,
	isEsport,
	purifyStringFromBoxBrackets,
	RegistrationMethodCode,
	removeMultipleDash,
	removeSpecialCharacter,
	virtualSportsEventType,
} from '../../shared/util/utility';
import { BetslipErrors, CouponData, CouponResultText, SlipType, TransactionDetails } from './interface';

export const TAXABLE_AKO_BONUS_VALUE = 2000;
export const TAXABLE_WIN_VALUE = 2280;

export const systemBetGroupNameArray = [
	'A',
	'B',
	'C',
	'D',
	'E',
	'F',
	'G',
	'H',
	'I',
	'J',
	'K',
	'L',
	'M',
	'N',
	'O',
	'P',
	'Q',
	'R',
	'S',
	'T',
	'U',
	'V',
	'W',
	'X',
	'Y',
	'Z',
	'{',
	'|',
	'}',
	'~',
];

export const checkIsGameModeChange = (outcomes: PlaceBet[], blocks: PlaceBet[][], filteredOutcomes?: PlaceBet[]): boolean => {
	const blocksCount = blocks.length;
	const outcomesCount = outcomes.length;
	const totalNumOfSlips = blocksCount + outcomesCount;
	const sureBetsCount = outcomes.filter((outcome) => outcome.banker).length;
	const notSureBetsCount = outcomesCount - sureBetsCount;

	if (totalNumOfSlips < 2) {
		return true;
	}

	if (sureBetsCount === notSureBetsCount && blocksCount === 0) {
		return filteredOutcomes ? filteredOutcomes.length > sureBetsCount && notSureBetsCount < 2 : false;
	}

	return (
		(sureBetsCount === outcomesCount && blocksCount === 0) ||
		(sureBetsCount === outcomesCount && blocksCount === 1) ||
		(sureBetsCount > notSureBetsCount && notSureBetsCount < 2 && blocksCount === 0) ||
		(sureBetsCount === filteredOutcomes?.length && blocksCount === 0)
	);
};

export const getBetTypeForSelectedBets = (outcomes: PlaceBet[]) => {
	const selectedBets = outcomes.filter((outcome) => outcome.selectedBet);
	return selectedBets.length === 1 ? SlipType.SOLO : SlipType.AKO;
};

export const getOutcomesAndBlocksAfterChange = (filteredBlocks: PlaceBet[][], filteredOutcomes: PlaceBet[]) => {
	const slipsInBetslip = [...filteredBlocks, ...filteredOutcomes];

	const outcomes: PlaceBet[] = [];
	const blocks: PlaceBet[][] = [];

	slipsInBetslip.forEach((group) => {
		if (Array.isArray(group)) {
			if (group.length > 1) {
				blocks.push(group);
			} else if (group.length === 1) {
				// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
				outcomes.push(group[0]!);
			}
		} else {
			outcomes.push(group);
		}
	});

	return { outcomes, blocks };
};

export const formatCouponHistoryDataByBlockId = (transactionDetails: TransactionDetails[]): TransactionDetails[][] => {
	const getDetailsByBlockId = transactionDetails?.reduce((detailsObj: any, detail: TransactionDetails, index: number) => {
		const blockId = (detail.blockId ??= -index);
		const groupKey: number = blockId;

		detailsObj[groupKey] ??= [];
		detailsObj[groupKey].push(detail);

		return detailsObj;
	}, {});

	return Object.values(getDetailsByBlockId || {});
};

export const getSystemBetsDetails = (allTransactionDetails: TransactionDetails[]) => {
	const detailsWithBanker = allTransactionDetails?.filter((detail) => detail.banker);
	const detailsWithoutBanker = allTransactionDetails?.filter((detail) => !detail.banker);
	const detailsByBlockId = formatCouponHistoryDataByBlockId(detailsWithoutBanker);
	return { detailsWithBanker, detailsWithoutBanker, detailsByBlockId };
};

export const getGroupNameFromBlockId = (allTransactionDetails: TransactionDetails[], detail: TransactionDetails[]) => {
	const { detailsByBlockId } = getSystemBetsDetails(allTransactionDetails);

	const blockIdOfCur = detail.map((detail) => detail.blockId);

	const indexOfBlock = detailsByBlockId.findIndex((blockDetail) => blockDetail.find((detail) => blockIdOfCur[0] === detail.blockId));

	return systemBetGroupNameArray[indexOfBlock];
};

export const isAko = (slipType: SlipType) => slipType === SlipType.AKO || slipType === SlipType.SOLO;

export const findOutCouponStatus = (couponData: CouponData): CouponResultText =>
	couponData.resultText || CouponStatusCodeToStatusMap[couponData.statusCode];

export const getCouponWinningValue = (couponData: CouponData) => {
	const status = findOutCouponStatus(couponData);
	if (status === CouponResultText.WON || status === CouponResultText.CASHOUT) {
		return couponData.confirmedReturn;
	} else if (status === CouponResultText.PLACED || status === CouponResultText.LOST) {
		return couponData.possibleReturn - couponData.tax;
	} else if (status === CouponResultText.CANCELLED) {
		return couponData.stake;
	} else {
		return 0;
	}
};

const participantUrl = (slipData: TransactionDetails) => {
	const participants = getParticipants(slipData);
	if (participants.length === 1) {
		// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
		return removeMultipleDash(removeSpecialCharacter(participants[0]!));
	}
	// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
	return removeMultipleDash(`${removeSpecialCharacter(participants[0]!)}-${removeSpecialCharacter(participants[1]!)}`);
};

const getRedirectionPageType = (event: PlaceBet | TransactionDetails) => (isEsport(event) ? APP_URL.eSport : APP_URL.sport);

export const getLiveEventUrl = (category: string, eventName: string, eventId: number) => {
	const categorySlug = removeSpecialCharacter(category);
	const eventNameSlug = removeSpecialCharacter(eventName);

	return removeMultipleDash(`${APP_URL.live}/${categorySlug}/${eventNameSlug}/${eventId}`);
};

const getPrematchEventUrl = (
	pageType: string,
	category1: string,
	category2: string,
	category3: string,
	eventName: string,
	eventId: number,
	leagueName?: string,
	isBetBuilder?: boolean,
) => {
	const category1Slug = removeSpecialCharacter(category1);
	const category2Slug = removeSpecialCharacter(category2);
	const category3Slug = removeSpecialCharacter(category3);

	const categories = leagueName ? leagueName : `${category1Slug}/${category2Slug}/${category3Slug}`;

	return removeMultipleDash(`${pageType}/${categories}/${eventName}/${eventId}`);
};

export const handleRedirectionToEventPage = (slipData: TransactionDetails): string => {
	if (slipData.statusCode !== CouponDetailStatusCode.IN_GAME || virtualSportsEventType.includes(slipData.eventType)) {
		return '';
	}

	const eventName = participantUrl(slipData);
	if (slipData.eventType === EventType.LIVE) {
		return getLiveEventUrl(slipData.categoryName3, eventName, slipData.eventId);
	}

	// prematch which goes live
	if (slipData.eventStart < Date.now() && slipData.liveEventId) {
		return getLiveEventUrl(slipData.categoryName3, eventName, slipData.liveEventId);
	}

	const pageType = getRedirectionPageType(slipData);
	return getPrematchEventUrl(pageType, slipData.categoryName1, slipData.categoryName2, slipData.categoryName3, eventName, slipData.eventId);
};

export const getRedirectionPathFromBetslip = (bet: PlaceBet): string => {
	if (virtualSportsEventType.includes(bet.eventType) || bet.isFinished) {
		return '';
	}

	if (bet.isLive) {
		const leagueName = bet.league.split('/').splice(-1).toString();
		return getLiveEventUrl(leagueName, bet.eventName, bet.eventId);
	}

	const leagueName = () => {
		const transformedLeagueName = removeSpecialCharacter(bet.league.split('/').toString());

		if (transformedLeagueName.includes('---')) {
			return transformedLeagueName.replace('---', '-').split('--').join('/');
		}
		return transformedLeagueName.split('--').join('/');
	};
	const pageType = getRedirectionPageType(bet);
	const eventName = removeSpecialCharacter(bet.eventName);

	return getPrematchEventUrl(pageType, '', '', '', eventName, bet.eventId, leagueName(), bet.isBetBuilder);
};

export const getBetslipErrorObj = (t: TFunction, errorCode: number, additionalDetails?: Object) => {
	const message = !!errorCode ? `error${errorCode}` : 'error';

	if (additionalDetails) {
		return { message: t(message, { ...additionalDetails }), code: errorCode };
	}

	return { message: t(message), code: errorCode };
};

export const getAllowedRegMethodName = (t: TFunction, allowedRegMethod?: string) => {
	const purifiedAllowedRegMethod = Number(purifyStringFromBoxBrackets(allowedRegMethod));

	switch (purifiedAllowedRegMethod) {
		case RegistrationMethodCode.ANDROID_APP:
			return t('androidApp');
		case RegistrationMethodCode.IOS_APP:
			return t('iosApp');
		case RegistrationMethodCode.MOBILE_BROWSER:
			return t('mobileBrowser');
		case RegistrationMethodCode.TERMINAL:
			return t('terminalMachine');
		case RegistrationMethodCode.WEB_BROWSER:
			return t('webBrowser');
		default:
			return '';
	}
};

export const getSlipTypeName = (t: TFunction, allowedSlipType?: string) => {
	const purifiedAllowedSlipType = Number(purifyStringFromBoxBrackets(allowedSlipType));

	switch (purifiedAllowedSlipType) {
		case SlipType.AKO:
			return t('akoBetType');
		case SlipType.SOLO:
			return t('soloBetType');
		case SlipType.SYSTEM:
			return t('systemBetType');
		default:
			return '';
	}
};

export const getEventTypeName = (t: TFunction, allowedEventType?: string) => {
	const purifiedAllowedEventType = Number(purifyStringFromBoxBrackets(allowedEventType));

	switch (purifiedAllowedEventType) {
		case EventType.PREMATCH:
			return t('prematch');
		case EventType.LIVE:
			return t('live');
		case EventType.OUTRIGHT:
			return t('outrights');
		case EventType.VIRTUAL_BASKETBALL:
			return t('virtualBasketball');
		case EventType.VIRTUAL_DOG_RACING:
			return t('virtualDogRacing');
		case EventType.VIRTUAL_FOOTBALL:
			return t('virtualFootball');
		case EventType.VIRTUAL_HORSE_RACING:
			return t('virtualHorseRacing');
		default:
			return '';
	}
};

export const renderCouponTextBasedOnResultText = (resultText: string) => {
	switch (resultText) {
		case CouponResultText.PLACED:
			return 'toWin';
		case CouponResultText.LOST:
			return 'win';
		case CouponResultText.WON:
			return 'win';
		case CouponResultText.CANCELLED:
			return 'refund';
		default:
			return 'amount';
	}
};

export const dangerousBetslipErrors = [
	BetslipErrors.CUSTOMER_STAKE_LIMIT_REACHED,
	BetslipErrors.CUSTOMER_POSSIBLE_RETURN_LIMIT_REACHED,
	BetslipErrors.ACCOUNT_CLOSED,
	BetslipErrors.BETTING_DISABLED,
	BetslipErrors.BETTING_DISABLED_WHEN_ACCOUNT_BLOCKED,
	BetslipErrors.IP_ADDRESS_IS_BLOCKED,
	BetslipErrors.REJECTED_BY_BETRADAR_MTS,
	BetslipErrors.CUSTOMER_DAILY_STAKE_LIMIT_REACHED,
	BetslipErrors.OFFER_FREEBET_VALIDATION_FAILED,
	BetslipErrors.MANAGED_TRADING_SERVICE_BET_REJECTED,
	BetslipErrors.TEMPORARY_ACCOUNT_TIME_LIMIT_REACHED,
];

export const getEmojiFromBetsCount = (numberOfBets: number) => {
	if (numberOfBets === 1) return '📝';
	if (numberOfBets === 2) return '✌️';
	if (numberOfBets === 3) return '🎶';
	if (numberOfBets === 4) return '🍀';
	if (numberOfBets >= 5 && numberOfBets <= 10) return '🔥';
	if (numberOfBets >= 11) return '💎';
	return '';
};

export const getUserOddsChangeRules = () => {
	const oddsChangeRule = localStorage.getItem('oddsChangeRule');
	switch (oddsChangeRule) {
		case OddsChangeRules.ACCEPT_ALL_ODDS_CHANGES:
			return { acceptAnyOdds: true, acceptHigherOdds: false, addAccumulator: false };
		case OddsChangeRules.ACCEPT_HIGHER_ODDS_CHANGES:
			return { acceptAnyOdds: false, acceptHigherOdds: true, addAccumulator: false };
		case OddsChangeRules.ACCEPT_NO_ODDS_CHANGES:
			return { acceptAnyOdds: false, acceptHigherOdds: false, addAccumulator: true };
		default:
			return { acceptAnyOdds: true, acceptHigherOdds: false, addAccumulator: false };
	}
};

export const formatCouponPrice = (price: number) => {
	const roundedPrice = Math.round(price * 100) / 100;

	const [integerPart, decimalPart] = roundedPrice.toString().split('.');
	// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
	const formattedInteger = integerPart!.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
	return decimalPart ? `${formattedInteger},${decimalPart.padEnd(2, '0')}` : formattedInteger.toString();
};
